(function () {
  'use strict';

  var MOBILE_MEDIA_QUERY = '(max-width: 768px)';
  var loadingTimer = null;
  var loadingStartedAt = 0;
  var lastHealthyCounts = {};
  var previousRefinements = [];
  var lastAppliedRefinement = null;
  var zeroValueCache = {};
  var sortTrigger = null;
  var filterInteractionTimer = null;
  var secondaryImageCache = {};
  var secondaryImagePreloadCache = {};
  var productImageWidths = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200];
  var productImageSizes = '(max-width: 768px) calc((100vw - 44px) / 2 - 2px), calc((min(100vw, 1440px) - 396px) / 4 - 2px)';

  function readConfig() {
    var element = document.getElementById('ezoko-search-config');

    if (!element) {
      return null;
    }

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('EZOKO InstantSearch configuration could not be read.', error);
      return null;
    }
  }

  function registerHook(algolia, hookName, callback) {
    if (algolia.hooks.allowedHooks.indexOf(hookName) === -1) {
      console.warn('EZOKO InstantSearch skipped unsupported Algolia hook:', hookName);
      return;
    }

    algolia.hooks.registerHook(hookName, callback);
  }

  function isMobile() {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  }

  function replaceToken(value, token, replacement) {
    return String(value || '').replace(token, String(replacement));
  }

  function formatNumber(value) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en').format(Number(value || 0));
  }

  function formatCount(pattern, count) {
    return replaceToken(pattern, '__COUNT__', formatNumber(count));
  }

  function escapeAttribute(value) {
    return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function safeDomId(value) {
    return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function facetTemplate(html, facet, index) {
    var collapsed = isMobile() || index > 3;
    var facetId = 'ezoko-facet-' + facet.escapedName;

    return html`
      <section class="ais-facet-dropdown-wrapper ais-facet-${facet.type} ais-facet-${facet.escapedName} filter-group" data-ezoko-facet="${facet.escapedName}">
        <label for="${facetId}" class="ais-range-slider--header ais-facet--header ais-header filter-group-header">
          <span class="title">
            <span class="label">${facet.title}<span class="applied-count" data-ezoko-facet-count></span></span>
            <span class="rule" aria-hidden="true"></span>
          </span>
          <svg class="chevron" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 8l4-4 4 4"></path></svg>
        </label>
        <input class="ais-dropdown-checkbox" type="checkbox" id="${facetId}" name="dropdown" checked="${collapsed}" />
        <div class="ais-facet-${facet.escapedName}-container ais-facet-dropdown-container"></div>
      </section>
    `;
  }

  function mainTemplate(algolia, config, data, html) {
    var translations = config.instantSearch;

    return html`
      <div class="ais-page ezoko-instantsearch-page">
        <div class="ezoko-instantsearch-bridge" aria-hidden="true" inert>
          <div class="ais-input"><div class="ais-search-box-container"></div><div class="ais-clear-input-icon"></div></div>
          <button type="button" class="ais-facets-button" tabindex="-1"></button>
          <div class="ais-facet-price_range-container" hidden></div>
          <div class="ais-search-header">
            <span class="ais-change-display-block ais-change-display-selected" role="button" aria-pressed="true" aria-label="grid display"></span>
            <span class="ais-change-display-list" role="button" aria-pressed="false" aria-label="list display"></span>
          </div>
        </div>
        <header class="results-header">
          <span class="search-eyebrow">${translations.eyebrow}</span>
          <div class="results-header-row">
            <h1 class="search-query" data-ezoko-search-query></h1>
            <div class="ais-stats-container results-count" aria-live="polite"></div>
          </div>
        </header>
        <div class="results-toolbar">
          <div class="applied-filters">
            <div class="ais-current-refined-values-container"></div>
            <div class="ais-clear-refinements-container"></div>
            <button type="button" class="clear-all" data-ezoko-toolbar-clear hidden>${translations.clearAll}</button>
          </div>
          <div class="ais-sort sort-control" data-ezoko-desktop-sort>
            ${data.multipleSortOrders
              ? html`
                  <span class="ais-sort-orders-container"></span>
                  <button type="button" class="desktop-sort-toggle" data-ezoko-toggle-desktop-sort aria-haspopup="listbox" aria-controls="ezoko-desktop-sort-menu" aria-expanded="false">
                    <span class="sort-label">${translations.sort}</span>
                    <span class="sort-value" data-ezoko-desktop-sort-label>${translations.relevance}</span>
                    <svg class="chevron" width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 4l4 4 4-4"></path></svg>
                  </button>
                  <div class="sort-menu" id="ezoko-desktop-sort-menu" role="listbox" data-ezoko-desktop-sort-options hidden></div>
                `
              : html`
                  <span class="sort-label">${translations.sort}</span>
                  <span class="sort-value">${algolia.translations.relevance}</span>
                `}
          </div>
        </div>
        <div class="mobile-results-toolbar">
          <button type="button" class="filter-trigger" data-ezoko-open-filters aria-controls="ezoko-search-filters" aria-expanded="false">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M2 4h12M4.5 8h7M6.5 12h3"></path></svg>
            <span class="label">${translations.filter}</span>
            <span class="trigger-count" data-ezoko-filter-count hidden>0</span>
          </button>
          <span class="divider" aria-hidden="true"></span>
          <button type="button" class="sort-trigger" data-ezoko-open-sort aria-controls="ezoko-mobile-sort" aria-expanded="false">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 3v10M5 13l-2.5-2.5M5 13l2.5-2.5M11 13V3M11 3L8.5 5.5M11 3l2.5 2.5"></path></svg>
            <span class="label">${translations.sort}</span>
            <span class="current" data-ezoko-sort-label></span>
          </button>
        </div>
        <div class="mobile-applied-filters"></div>
        <div class="results-layout">
          <aside class="ais-facets filters-sidebar" id="ezoko-search-filters" aria-label="${translations.filters}">
            <div class="filter-panel-header mobile-filter-header">
              <button type="button" class="close-filters" data-ezoko-close-filters aria-label="${translations.closeFilters}">
                <svg width="15" height="15" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8"></path></svg>
              </button>
              <span class="title">${translations.filters}</span>
              <button type="button" class="clear-all" data-ezoko-clear-filters>${translations.clearAll}<span data-ezoko-clear-filter-count></span></button>
            </div>
            <div class="filter-body">
              ${algolia.config.app_blocks_dynamic_widgets_beta_enabled
                ? html`<div class="ais-dynamic-widgets-container"></div>`
                : data.facets.filter(function (facet) { return facet.name !== 'price_range'; }).map(function (facet, index) { return facetTemplate(html, facet, index); })}
            </div>
            <button type="button" class="show-results" data-ezoko-close-filters>
              <span data-ezoko-show-results>${formatCount(translations.showResults, 0)}</span>
              <svg class="arrow" width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 7h10M8 3l4 4-4 4"></path></svg>
            </button>
          </aside>
          <main class="ais-block products-column">
            <div class="ais-hits-container ais-results-as-block products-grid"></div>
            <nav class="ais-pagination-container pagination" aria-label="${translations.pageNavigation}"></nav>
          </main>
        </div>
        <div class="mobile-sort-layer" id="ezoko-mobile-sort" aria-hidden="true">
          <button type="button" class="sheet-backdrop" data-ezoko-close-sort aria-label="${translations.closeFilters}"></button>
          <div class="mobile-sort" role="dialog" aria-modal="true" aria-label="${translations.sort}">
            <span class="sheet-handle" aria-hidden="true"></span>
            <span class="section-header">${translations.sort}</span>
            <div data-ezoko-sort-options></div>
          </div>
        </div>
      </div>
    `;
  }

  function productTemplate(algolia, config, hit, html, components, handleItemClick) {
    var image = algolia.helpers.renderImage(hit, 520);
    var primarySource = image.src.replace(/_520x(?=\.[a-z0-9]{2,4}(?:[?#]|$))/i, '');
    var secondaryImage = secondaryImageForHit(hit, image.src);
    var itemLink = algolia.helpers.instantsearchLink(hit);
    var productUrl = '/products/' + hit.handle;
    var comparePrice = algolia.helpers.displayStrikedPrice(hit.price, hit.compare_at_price, hit.currency);
    var hasSalePrice = Boolean(comparePrice);
    var optionCount = Number(hit.variants_count || 0);
    var fullTitle = algolia.helpers.fullTitle(hit.title, hit._distinct || hit._productLevel, hit.variant_title);
    var productCard = config.productCard || {};
    var quickBuyEnabled = Boolean(productCard.enableQuickBuy);
    var selectedVariantCanBePurchased = hitCanBePurchased(hit);
    var quickAddEnabled = quickBuyEnabled && (selectedVariantCanBePurchased || optionCount > 1) && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var quickAddAvailability = selectedVariantCanBePurchased ? 'available' : (optionCount > 1 ? 'verify' : 'unavailable');
    var quickBuyId = 'ezoko-search-product-' + safeDomId(hit.id) + '-' + safeDomId(hit.objectID);

    if (productCard.showSecondaryImage && !secondaryImage && window.matchMedia('(pointer: fine)').matches) {
      preloadSecondaryImageForHit(hit, image.src, productUrl);
    }

    return html`
      <article
        class="ais-hit product-result"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-distinct="${hit._distinct}"
        data-product-id="${hit.id}"
        data-ezoko-quick-add-availability="${quickAddAvailability}"
        onClick="${function (event) {
          if (event.target.closest('.product-result-link')) {
            handleItemClick(hit);
          }
        }}"
      >
        <span class="product-image product-item__image-wrapper" data-ezoko-image-wrapper data-product-url="${productUrl}">
          <a class="product-result-link product-image-link product-item__aspect-ratio" href="${itemLink}" title="${fullTitle}">
            <img
              class="product-item__primary-image"
              src="${imageUrlAtWidth(primarySource, 600)}"
              srcset="${productImageWidths.map(function (width) { return imageUrlAtWidth(primarySource, width) + ' ' + width + 'w'; }).join(',')}"
              sizes="${productImageSizes}"
              alt="${fullTitle}"
              loading="${Number(hit.productPosition) <= 4 ? 'eager' : 'lazy'}"
              fetchpriority="${Number(hit.productPosition) <= 1 ? 'high' : 'auto'}"
              width="520"
              height="520"
            />
            ${secondaryImage
              ? html`
                  <img
                    class="product-item__secondary-image"
                    src="${imageUrlAtWidth(secondaryImage.src, 520)}"
                    srcset="${productImageWidths.map(function (width) { return imageUrlAtWidth(secondaryImage.src, width) + ' ' + width + 'w'; }).join(',')}"
                    sizes="${productImageSizes}"
                    alt=""
                    loading="eager"
                    fetchpriority="auto"
                    width="${secondaryImage.width || 520}"
                    height="${secondaryImage.height || 520}"
                  />
                `
              : null}
            ${hasSalePrice ? html`<span class="product-badge is-sale">${config.instantSearch.sale}</span>` : null}
          </a>
          ${quickAddEnabled
            ? html`
                <button type="button" is="toggle-button" aria-controls="${quickBuyId}-drawer" aria-expanded="false" aria-label="${productCard.quickAdd}: ${fullTitle}" class="button ezoko-quick-add" data-ezoko-quick-add>
                  <span class="ezoko-quick-add__label">${productCard.quickAdd}</span>
                  <span class="ezoko-quick-add__spinner" aria-hidden="true"></span>
                </button>
              `
            : null}
        </span>
        <a class="product-result-link product-details-link" href="${itemLink}" title="${fullTitle}">
          <span class="product-vendor-band">
            ${hit.vendor ? html`<span class="product-vendor">${components.Highlight({ hit: hit, attribute: 'vendor' })}</span>` : null}
          </span>
          <span class="product-info">
            <span class="product-title">${components.Highlight({ hit: hit, attribute: 'title' })}${algolia.helpers.variantTitleAddition(hit, hit._distinct || hit._productLevel)}</span>
            <span class="product-price-row">
              <span class="product-price ${hasSalePrice ? 'is-sale' : ''}">
                ${algolia.helpers.displayPrice(hit, hit._distinct, hit._productLevel)}
                ${comparePrice ? html`<span class="product-compare-price">${comparePrice}</span>` : null}
              </span>
              ${!hasSalePrice && optionCount > 1 ? html`<span class="product-meta">${formatCount(config.translations.optionCount, optionCount)}</span>` : null}
            </span>
          </span>
        </a>
        ${quickAddEnabled
          ? html`
              <quick-buy-drawer id="${quickBuyId}-drawer" href="${productUrl}" class="drawer drawer--large drawer--quick-buy"></quick-buy-drawer>
            `
          : null}
      </article>
    `;
  }

  function hitCanBePurchased(hit) {
    if (typeof hit.can_order === 'boolean') {
      return hit.can_order;
    }

    if (typeof hit.inventory_available === 'boolean') {
      return hit.inventory_available;
    }

    var inventoryPolicy = String(hit.inventory_policy || '').toLowerCase();

    if (inventoryPolicy === 'allow' || inventoryPolicy === 'continue') {
      return true;
    }

    if (typeof hit.inventory_quantity !== 'undefined') {
      return Number(hit.inventory_quantity) > 0;
    }

    return true;
  }

  function productCanBePurchased(product) {
    if (typeof product.available === 'boolean') {
      return product.available;
    }

    if (Array.isArray(product.variants)) {
      return product.variants.some(function (variant) {
        return variant.available;
      });
    }

    return true;
  }

  function statsTemplate(config, data, html) {
    return html`
      <span class="ezoko-stats" data-query="${data.query || ''}" data-nb-hits="${data.nbHits || 0}" data-status="ready">
        ${formatCount(config.instantSearch.products, data.nbHits || 0)}
      </span>
    `;
  }

  function noResultsTemplate(config, html) {
    return html`
      <div class="ais-hit-empty ezoko-no-results" data-ezoko-no-results>
        <div data-ezoko-empty-content></div>
      </div>
    `;
  }

  function enhanceFacetParams(params, facet, config) {
    var originalTransform = params.transformItems;
    var cacheKey = facet.name;

    if (facet.type === 'slider') {
      return params;
    }

    if (facet.name === 'vendor') {
      params.operator = 'or';
    }

    params.limit = isMobile() ? 3 : 5;
    params.showMore = facet.type !== 'slider';
    params.showMoreLimit = Math.max(Number(params.showMoreLimit || 0), 10);

    if (params.showMore) {
      params.templates = Object.assign({}, params.templates, {
        showMoreText: function (data) {
          return data.isShowingMore ? config.instantSearch.showLess : config.instantSearch.showMore;
        }
      });
    }

    if (facet.searchable) {
      params.searchablePlaceholder = replaceToken(config.instantSearch.facetSearch, '__FACET__', facet.title || facet.name);
    }

    params.transformItems = function (items, context) {
      var transformed = originalTransform ? originalTransform(items, context) : items;
      var query = context && context.results ? context.results.query || '' : '';
      var keyedCache = zeroValueCache[query] || (zeroValueCache[query] = {});
      var cachedItems = keyedCache[cacheKey] || [];
      var currentValues = {};

      transformed.forEach(function (item) {
        currentValues[String(item.value)] = true;
      });

      cachedItems.forEach(function (item) {
        if (!currentValues[String(item.value)]) {
          transformed.push(Object.assign({}, item, { count: 0, isRefined: false, __ezokoZero: true }));
        }
      });

      keyedCache[cacheKey] = transformed.map(function (item) {
        return Object.assign({}, item, { __ezokoZero: false });
      });

      return transformed;
    };

    return params;
  }

  function selectedFacetLabel(attribute) {
    var group = document.querySelector('.ais-facet-' + CSS.escape(attribute));
    var label = group && group.querySelector('.ais-RefinementList-item--selected .ais-RefinementList-label');

    if (!label) {
      return '';
    }

    return Array.prototype.filter.call(label.childNodes, function (node) {
      return node.nodeType === Node.TEXT_NODE;
    }).map(function (node) {
      return node.textContent;
    }).join('').trim();
  }

  function refinementData() {
    return Array.prototype.map.call(document.querySelectorAll('.results-toolbar .ais-current-refined-values--link'), function (link) {
      var label = decodeURIComponent(link.dataset.label || link.dataset.value || link.textContent.trim());

      if (label.indexOf(':') !== -1) {
        label = selectedFacetLabel(link.dataset.attribute) || label;
      }

      return {
        key: [link.dataset.attribute, link.dataset.type, link.dataset.value].join('|'),
        label: label,
        link: link
      };
    });
  }

  function syncRefinementHistory(refinements) {
    var previousKeys = previousRefinements.map(function (item) { return item.key; });
    var added = refinements.filter(function (item) { return previousKeys.indexOf(item.key) === -1; });

    if (added.length === 1) {
      lastAppliedRefinement = added[0];
    } else if (lastAppliedRefinement && !refinements.some(function (item) { return item.key === lastAppliedRefinement.key; })) {
      lastAppliedRefinement = null;
    }

    previousRefinements = refinements.map(function (item) {
      return { key: item.key, label: item.label };
    });
  }

  function featuredCollectionsMarkup(config) {
    var collections = (config.featuredCollections || []).filter(function (collection) {
      return collection && collection.title && collection.url;
    });

    if (!collections.length) {
      return '';
    }

    return '<section class="recovery-actions"><h3 class="section-header">' + escapeAttribute(config.instantSearch.featuredCollections) + '</h3><div class="collection-tiles">' + collections.map(function (collection) {
      var image = collection.image ? '<img src="' + escapeAttribute(collection.image) + '" alt="" loading="lazy" width="360" height="208">' : '<span class="collection-tile-placeholder" aria-hidden="true"></span>';
      return '<a class="collection-tile" href="' + escapeAttribute(collection.url) + '">' + image + '<span class="scrim" aria-hidden="true"></span><span class="tile-label">' + escapeAttribute(collection.title) + '</span></a>';
    }).join('') + '</div></section>';
  }

  function renderEmptyState(config, query, count, refinements) {
    var container = document.querySelector('[data-ezoko-empty-content]');

    if (!container) {
      return;
    }

    if (!refinements.length) {
      container.innerHTML = '<div class="no-results"><div><h2 class="no-results-title">' + replaceToken(escapeAttribute(config.instantSearch.noMatches), '__QUERY__', escapeAttribute(query)) + '</h2><p class="no-results-hint">' + escapeAttribute(config.instantSearch.noResultsHint) + '</p></div>' + featuredCollectionsMarkup(config) + '</div>';
      return;
    }

    var baseCount = lastHealthyCounts[query];
    var summary = baseCount ? formatCount(config.instantSearch.filteredResultsOf, baseCount) : config.instantSearch.filteredResults;
    var culprit = lastAppliedRefinement && refinements.some(function (item) { return item.key === lastAppliedRefinement.key; }) ? lastAppliedRefinement : null;
    var culpritMarkup = culprit ? '<div class="try-removing"><span>' + escapeAttribute(config.instantSearch.tryRemoving) + '</span><button type="button" class="culprit-filter" data-ezoko-remove-last="' + escapeAttribute(culprit.key) + '">' + escapeAttribute(culprit.label) + '<span class="applied-filter-remove" aria-hidden="true">×</span></button></div>' : '';

    container.innerHTML = '<div class="filtered-no-results"><div><h2 class="no-results-title">' + escapeAttribute(config.instantSearch.noFilteredResults) + '</h2><p class="no-results-hint">' + escapeAttribute(summary) + '</p></div>' + culpritMarkup + '<button type="button" class="clear-filters-button ais-hit-empty--clear-filters">' + escapeAttribute(config.instantSearch.clearAllFilters) + '<span class="arrow" aria-hidden="true">→</span></button><p class="broader-link">' + escapeAttribute(config.instantSearch.broaderSearch) + '</p></div>';
  }

  function cloneAppliedFilters() {
    var source = document.querySelector('.results-toolbar .applied-filters');
    var target = document.querySelector('.mobile-applied-filters');

    if (!source || !target) {
      return;
    }

    target.innerHTML = source.innerHTML;
    target.querySelectorAll('[id]').forEach(function (element) { element.removeAttribute('id'); });
  }

  function enhanceAppliedFilters() {
    document.querySelectorAll('.ais-current-refined-values--link').forEach(function (link) {
      var label = decodeURIComponent(link.dataset.label || link.dataset.value || link.textContent.trim());

      if (label.indexOf(':') !== -1) {
        label = selectedFacetLabel(link.dataset.attribute) || label;
      }

      link.classList.add('applied-filter');
      link.setAttribute('aria-label', label);
      link.innerHTML = '<span class="value">' + escapeAttribute(label) + '</span><span class="applied-filter-remove" aria-hidden="true">×</span>';
    });

    document.querySelectorAll('.ais-ClearRefinements-button').forEach(function (button) {
      button.classList.add('clear-all');
    });
  }

  function enhanceFacets(refinements) {
    document.querySelectorAll('.filter-group').forEach(function (group) {
      var selectedCount = group.querySelectorAll('.ais-RefinementList-item--selected').length;
      var count = group.querySelector('[data-ezoko-facet-count]');

      if (count) {
        count.textContent = selectedCount ? ' · ' + selectedCount : '';
      }

      if (isMobile() && selectedCount) {
        var checkbox = group.querySelector('.ais-dropdown-checkbox');
        if (checkbox) {
          checkbox.checked = false;
        }
      }
    });

    document.querySelectorAll('.ais-RefinementList-label').forEach(function (label) {
      var item = label.closest('.ais-RefinementList-item');
      var countElement = label.querySelector('.ais-RefinementList-count');
      var count = countElement ? Number(countElement.textContent.replace(/[^0-9.-]/g, '')) : 0;
      label.setAttribute('role', 'checkbox');
      label.setAttribute('tabindex', count === 0 ? '-1' : '0');
      label.setAttribute('aria-checked', item && item.classList.contains('ais-RefinementList-item--selected') ? 'true' : 'false');

      if (item) {
        item.classList.toggle('is-disabled', count === 0);
      }
    });

    var total = refinements.length;
    document.querySelectorAll('[data-ezoko-filter-count]').forEach(function (element) {
      element.textContent = total;
      element.hidden = total === 0;
    });
    document.querySelectorAll('[data-ezoko-clear-filter-count]').forEach(function (element) {
      element.textContent = total ? ' (' + formatNumber(total) + ')' : '';
    });
  }

  function formatPriceValue(prefix, value) {
    return prefix + ' ' + formatNumber(Math.round(Number(value || 0)));
  }

  function parsePriceValue(value) {
    var parsed = Number(String(value || '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function applyPriceInputs(container, changedInput) {
    var minInput = container.querySelector('[data-ezoko-price-min]');
    var maxInput = container.querySelector('[data-ezoko-price-max]');
    var lowerHandle = container.parentElement.querySelector('.rheostat-handle-lower');
    var upperHandle = container.parentElement.querySelector('.rheostat-handle-upper');

    if (!minInput || !maxInput || !lowerHandle || !upperHandle) {
      return;
    }

    var absoluteMin = Number(lowerHandle.getAttribute('aria-valuemin'));
    var absoluteMax = Number(upperHandle.getAttribute('aria-valuemax'));
    var min = parsePriceValue(minInput.value);
    var max = parsePriceValue(maxInput.value);

    min = Math.max(absoluteMin, Math.min(min === null ? absoluteMin : min, absoluteMax));
    max = Math.max(absoluteMin, Math.min(max === null ? absoluteMax : max, absoluteMax));

    if (min > max) {
      if (changedInput === minInput) {
        min = max;
      } else {
        max = min;
      }
    }

    var url = new URL(window.location.href);
    var rangeMin = min > absoluteMin ? Math.round(min) : '';
    var rangeMax = max < absoluteMax ? Math.round(max) : '';

    if (rangeMin === '' && rangeMax === '') {
      url.searchParams.delete('range[price]');
    } else {
      url.searchParams.set('range[price]', rangeMin + ':' + rangeMax);
    }

    if (url.href !== window.location.href) {
      window.location.assign(url.href);
    }
  }

  function enhancePriceFacet(config) {
    var facet = document.querySelector('.ais-facet-price .ais-facet-dropdown-container');
    var rheostat = facet && facet.querySelector('.rheostat');
    var lowerHandle = rheostat && rheostat.querySelector('.rheostat-handle-lower');
    var upperHandle = rheostat && rheostat.querySelector('.rheostat-handle-upper');

    if (!facet || !rheostat || !lowerHandle || !upperHandle) {
      return;
    }

    var inputs = facet.querySelector('[data-ezoko-price-inputs]');

    if (!inputs) {
      inputs = document.createElement('div');
      inputs.className = 'ezoko-price-inputs';
      inputs.setAttribute('data-ezoko-price-inputs', '');
      inputs.innerHTML = '<input type="text" inputmode="decimal" data-ezoko-price-min><span class="separator" aria-hidden="true"></span><input type="text" inputmode="decimal" data-ezoko-price-max>';
      facet.appendChild(inputs);

      inputs.querySelectorAll('input').forEach(function (input) {
        input.addEventListener('focus', function () {
          input.select();
        });
        input.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            applyPriceInputs(inputs, input);
          }
        });
        input.addEventListener('change', function () {
          applyPriceInputs(inputs, input);
        });
      });
    }

    var prefix = config.productCard && config.productCard.currencyPrefix ? config.productCard.currencyPrefix : 'CA$';
    var minInput = inputs.querySelector('[data-ezoko-price-min]');
    var maxInput = inputs.querySelector('[data-ezoko-price-max]');

    if (document.activeElement !== minInput) {
      minInput.value = formatPriceValue(prefix, lowerHandle.getAttribute('aria-valuenow'));
      minInput.setAttribute('aria-label', lowerHandle.getAttribute('aria-label') || 'Minimum price');
    }
    if (document.activeElement !== maxInput) {
      maxInput.value = formatPriceValue(prefix, upperHandle.getAttribute('aria-valuenow'));
      maxInput.setAttribute('aria-label', upperHandle.getAttribute('aria-label') || 'Maximum price');
    }
  }

  function imageUrlAtWidth(source, width) {
    var url = new URL(source, window.location.origin);
    url.searchParams.set('width', width);
    return url.href;
  }

  function canonicalProductImageSource(source) {
    if (!source) {
      return '';
    }

    try {
      var url = new URL(source, window.location.origin);
      var pathname = decodeURIComponent(url.pathname);
      var extensionIndex = pathname.lastIndexOf('.');

      if (extensionIndex === -1) {
        return pathname;
      }

      var basename = pathname.slice(0, extensionIndex).replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x(?:\d+)?)(?:_crop_(?:top|center|bottom|left|right))?(?:@2x)?$/i, '');
      return basename + pathname.slice(extensionIndex).toLowerCase();
    } catch (error) {
      return String(source).split(/[?#]/)[0];
    }
  }

  function secondaryImageForHit(hit, primarySource) {
    var images = Array.isArray(hit.images) ? hit.images.map(function (image) {
      var source = typeof image === 'string' ? image : image && (image.url || image.src);

      if (!source) {
        return null;
      }

      return {
        src: source,
        width: image && image.width,
        height: image && image.height
      };
    }).filter(Boolean) : [];

    if (images.length < 2) {
      return null;
    }

    var primaryKey = canonicalProductImageSource(primarySource);
    var primaryIndex = images.findIndex(function (image) {
      return canonicalProductImageSource(image.src) === primaryKey;
    });
    var startIndex = primaryIndex >= 0 ? primaryIndex + 1 : 0;

    for (var offset = 0; offset < images.length; offset += 1) {
      var candidate = images[(startIndex + offset) % images.length];

      if (canonicalProductImageSource(candidate.src) !== primaryKey) {
        return candidate;
      }
    }

    return null;
  }

  function secondaryImageForProduct(product, primarySource, variantId) {
    var media = Array.isArray(product.media) ? product.media.filter(function (item) {
      return item.media_type === 'image' && item.preview_image && item.preview_image.src;
    }) : [];

    if (media.length < 2) {
      return null;
    }

    var renderedPrimaryKey = canonicalProductImageSource(primarySource);
    var matchingVariant = Array.isArray(product.variants) ? product.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    }) : null;
    var variantImage = matchingVariant && matchingVariant.featured_image;
    var variantPrimaryKey = variantImage && variantImage.src ? canonicalProductImageSource(variantImage.src) : '';
    var primaryKeys = [renderedPrimaryKey, variantPrimaryKey].filter(Boolean);
    var primaryIndex = media.findIndex(function (item) {
      return canonicalProductImageSource(item.preview_image.src) === (variantPrimaryKey || renderedPrimaryKey);
    });

    if (primaryIndex === -1 && variantImage && variantImage.position) {
      primaryIndex = media.findIndex(function (item) {
        return Number(item.position) === Number(variantImage.position);
      });
    }

    var startIndex = primaryIndex >= 0 ? primaryIndex + 1 : 0;

    for (var offset = 0; offset < media.length; offset += 1) {
      var candidate = media[(startIndex + offset) % media.length];
      var candidateKey = canonicalProductImageSource(candidate.preview_image.src);

      if (candidateKey && primaryKeys.indexOf(candidateKey) === -1) {
        return candidate.preview_image;
      }
    }

    return null;
  }

  function productMediaPromise(productUrl) {
    if (!secondaryImageCache[productUrl]) {
      secondaryImageCache[productUrl] = fetch(productUrl + '.js', { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) {
          throw new Error('Product media could not be loaded.');
        }
        return response.json();
      }).catch(function () {
        return null;
      });
    }

    return secondaryImageCache[productUrl];
  }

  function preloadSecondaryImageForHit(hit, primarySource, productUrl) {
    var preloadKey = [productUrl, hit.objectID, canonicalProductImageSource(primarySource)].join('|');

    if (secondaryImagePreloadCache[preloadKey]) {
      return;
    }

    secondaryImagePreloadCache[preloadKey] = productMediaPromise(productUrl).then(function (product) {
      var secondary = product && secondaryImageForProduct(product, primarySource, hit.objectID);

      if (!secondary) {
        return null;
      }

      var image = new Image();
      image.alt = '';
      image.loading = 'eager';
      image.fetchPriority = 'auto';
      image.sizes = productImageSizes;
      image.srcset = productImageWidths.map(function (width) { return imageUrlAtWidth(secondary.src, width) + ' ' + width + 'w'; }).join(',');
      image.src = imageUrlAtWidth(secondary.src, 520);

      if (typeof image.decode === 'function') {
        image.decode().catch(function () {});
      }

      return image;
    });
  }

  function loadSecondaryImage(wrapper) {
    var existingImage = wrapper.querySelector('.product-item__secondary-image');

    if (existingImage) {
      prepareSecondaryImage(wrapper, existingImage);
      return;
    }

    if (!wrapper.isConnected || wrapper.dataset.ezokoSecondaryLoaded === 'true') {
      return;
    }

    wrapper.dataset.ezokoSecondaryLoaded = 'true';
    var productUrl = wrapper.dataset.productUrl;
    var primary = wrapper.querySelector('.product-item__primary-image');

    if (!productUrl || !primary) {
      return;
    }

    productMediaPromise(productUrl).then(function (product) {
      if (!product) {
        return;
      }

      document.querySelectorAll('[data-ezoko-image-wrapper]').forEach(function (activeWrapper) {
        if (activeWrapper.dataset.productUrl === productUrl) {
          appendSecondaryImageFromProduct(activeWrapper, product);
        }
      });
    });
  }

  function appendSecondaryImageFromProduct(wrapper, product) {
    var existingImage = wrapper.querySelector('.product-item__secondary-image');

    if (existingImage) {
      prepareSecondaryImage(wrapper, existingImage);
      return;
    }

    var primary = wrapper.querySelector('.product-item__primary-image');
    var card = wrapper.closest('.product-result');

    if (!primary || !card) {
      return;
    }

    var secondary = secondaryImageForProduct(product, primary.currentSrc || primary.src, card.dataset.variantId);

    if (!secondary) {
      return;
    }

    var image = document.createElement('img');
    image.className = 'product-item__secondary-image';
    image.alt = '';
    image.loading = 'eager';
    image.fetchPriority = 'auto';
    image.width = secondary.width;
    image.height = secondary.height;
    image.sizes = primary.sizes;
    image.src = imageUrlAtWidth(secondary.src, 520);
    image.srcset = productImageWidths.map(function (width) { return imageUrlAtWidth(secondary.src, width) + ' ' + width + 'w'; }).join(',');
    wrapper.dataset.ezokoSecondaryLoaded = 'true';
    wrapper.querySelector('.product-image-link').appendChild(image);
    prepareSecondaryImage(wrapper, image);
  }

  function prepareSecondaryImage(wrapper, image) {
    if (!image || image.dataset.ezokoSecondaryPrepared === 'true') {
      return;
    }

    image.dataset.ezokoSecondaryPrepared = 'true';
    var markImageReady = function () {
      if (!wrapper.isConnected || image.dataset.ezokoSecondaryReady === 'true') {
        return;
      }

      image.dataset.ezokoSecondaryReady = 'true';
      wrapper.classList.add('product-item__image-wrapper--multiple');
    };
    var decodeImage = function () {
      if (typeof image.decode === 'function') {
        image.decode().catch(function () {}).then(markImageReady);
      } else {
        markImageReady();
      }
    };

    image.addEventListener('load', decodeImage, { once: true });
    image.addEventListener('error', function () {
      image.remove();
    }, { once: true });

    if (image.complete && image.naturalWidth > 0) {
      decodeImage();
    }
  }

  function preloadSecondaryImages(wrappers) {
    if (!window.matchMedia('(pointer: fine)').matches) {
      return;
    }

    wrappers.forEach(function (wrapper) {
      if (wrapper.dataset.ezokoSecondaryLoaded !== 'true') {
        loadSecondaryImage(wrapper);
      }
    });
  }

  function upgradeProductCardControls(card) {
    card.querySelectorAll('form[is="product-form"]').forEach(function (form) {
      if (typeof form._onSubmit === 'function') {
        return;
      }

      var upgradedForm = document.createElement('form', { is: 'product-form' });
      Array.from(form.attributes).forEach(function (attribute) {
        if (attribute.name !== 'data-ezoko-values-bound') {
          upgradedForm.setAttribute(attribute.name, attribute.value);
        }
      });
      while (form.firstChild) {
        upgradedForm.appendChild(form.firstChild);
      }
      form.replaceWith(upgradedForm);
    });

    card.querySelectorAll('button[is="loader-button"]').forEach(function (button) {
      var behavior = button.getAttribute('is');
      var isUpgraded = Boolean(button.querySelector('.loader-button__text'));

      if (isUpgraded) {
        return;
      }

      var upgradedButton = document.createElement('button', { is: behavior });
      Array.from(button.attributes).forEach(function (attribute) {
        upgradedButton.setAttribute(attribute.name, attribute.value);
      });
      while (button.firstChild) {
        upgradedButton.appendChild(button.firstChild);
      }

      button.replaceWith(upgradedButton);
    });
  }

  function syncDirectProductForm(card) {
    var form = card.querySelector('.product-image > form[is="product-form"]');

    if (!form) {
      return;
    }

    var values = {
      form_type: 'product',
      utf8: '✓',
      quantity: '1',
      id: card.dataset.variantId || '',
      product_id: card.dataset.productId || ''
    };

    Object.keys(values).forEach(function (name) {
      var input = form.querySelector('[name="' + name + '"]');
      if (input) {
        input.value = values[name];
        input.setAttribute('value', values[name]);
      }
    });

    if (form.dataset.ezokoValuesBound !== 'true') {
      form.dataset.ezokoValuesBound = 'true';
      form.addEventListener('submit', function () {
        syncDirectProductForm(card);
      }, true);
    }
  }

  function clearQuickAddLoading(button) {
    window.clearTimeout(button.ezokoQuickAddLoadingTimer);
    window.clearTimeout(button.ezokoQuickAddLoadingSafetyTimer);
    button.classList.remove('is-loading');
    button.disabled = false;
  }

  function bindQuickAddLoading(button, target) {
    if (button.dataset.ezokoQuickAddLoadingBound === 'true') {
      return;
    }

    button.dataset.ezokoQuickAddLoadingBound = 'true';

    target.addEventListener('openable-element:load:start', function () {
      clearQuickAddLoading(button);

      var buttonWidth = Math.ceil(button.getBoundingClientRect().width);
      if (buttonWidth) {
        button.style.setProperty('--ezoko-quick-add-min-width', buttonWidth + 'px');
      }

      button.disabled = true;
      button.ezokoQuickAddLoadingTimer = window.setTimeout(function () {
        button.classList.add('is-loading');
      }, 300);
      button.ezokoQuickAddLoadingSafetyTimer = window.setTimeout(function () {
        clearQuickAddLoading(button);
        button.removeAttribute('aria-busy');
      }, 15000);
    });

    target.addEventListener('openable-element:load:end', function () {
      clearQuickAddLoading(button);
    });

    target.addEventListener('openable-element:open', function () {
      clearQuickAddLoading(button);
    });
  }

  function bindQuickBuyButtons(card) {
    card.querySelectorAll('button[is="toggle-button"]').forEach(function (button) {
      var targetId = button.getAttribute('aria-controls');
      var target = targetId && document.getElementById(targetId);

      if (button.matches('[data-ezoko-quick-add]') && target) {
        bindQuickAddLoading(button, target);
      }

      if (button.dataset.ezokoQuickBuyBound === 'true') {
        return;
      }

      button.dataset.ezokoQuickBuyBound = 'true';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        var controlledId = button.getAttribute('aria-controls');
        var controlledElement = controlledId && document.getElementById(controlledId);

        if (!controlledElement || !window.customElements) {
          return;
        }

        Promise.all([
          window.customElements.whenDefined('toggle-button'),
          window.customElements.whenDefined(controlledElement.localName)
        ]).then(function () {
          if (!button.isConnected || !controlledElement.isConnected) {
            return;
          }

          window.customElements.upgrade(button);
          window.customElements.upgrade(controlledElement);
          button.setAttribute('aria-expanded', 'true');

          if (controlledElement.dataset.ezokoQuickBuyBound !== 'true') {
            controlledElement.dataset.ezokoQuickBuyBound = 'true';
            controlledElement.addEventListener('openable-element:close', function () {
              document.querySelectorAll('[aria-controls="' + CSS.escape(controlledElement.id) + '"]').forEach(function (control) {
                control.setAttribute('aria-expanded', 'false');
              });
            });
          }
        });
      }, true);
    });
  }

  function verifyQuickAddAvailability(card) {
    if (card.dataset.ezokoQuickAddAvailability !== 'verify' || card.dataset.ezokoQuickAddAvailabilityCheck === 'pending') {
      return;
    }

    var button = card.querySelector('[data-ezoko-quick-add]');
    var wrapper = card.querySelector('[data-ezoko-image-wrapper]');
    var productUrl = wrapper && wrapper.dataset.productUrl;

    if (!button || !productUrl) {
      return;
    }

    card.dataset.ezokoQuickAddAvailabilityCheck = 'pending';
    productMediaPromise(productUrl).then(function (product) {
      if (!card.isConnected || !product) {
        delete card.dataset.ezokoQuickAddAvailabilityCheck;
        return;
      }

      var canBePurchased = productCanBePurchased(product);
      card.dataset.ezokoQuickAddAvailability = canBePurchased ? 'available' : 'unavailable';
      card.dataset.ezokoQuickAddAvailabilityCheck = 'complete';

      if (canBePurchased) {
        return;
      }

      var targetId = button.getAttribute('aria-controls');
      var target = targetId && document.getElementById(targetId);
      button.remove();

      if (target) {
        target.remove();
      }
    });
  }

  function portalizeQuickBuyElements(cards) {
    var activeTargetIds = [];
    var portaledById = {};

    document.querySelectorAll('[data-ezoko-instantsearch-quick-buy]').forEach(function (element) {
      portaledById[element.id] = element;
    });

    cards.forEach(function (card) {
      card.querySelectorAll('button[aria-controls]').forEach(function (button) {
        var targetId = button.getAttribute('aria-controls');
        if (targetId && activeTargetIds.indexOf(targetId) === -1) {
          activeTargetIds.push(targetId);
        }
      });

      card.querySelectorAll('quick-buy-drawer, quick-buy-popover').forEach(function (element) {
        var previousElement = portaledById[element.id];

        if (previousElement && previousElement !== element) {
          previousElement.remove();
        }

        element.dataset.ezokoInstantsearchQuickBuy = 'true';
        document.body.appendChild(element);
        portaledById[element.id] = element;
      });
    });

    Object.keys(portaledById).forEach(function (id) {
      var element = portaledById[id];

      if (activeTargetIds.indexOf(id) === -1) {
        if (element.hasAttribute('open')) {
          element.open = false;
        }
        element.remove();
      }
    });
  }

  function enhanceProductCards(config) {
    var productCard = config.productCard || {};
    var cards = Array.from(document.querySelectorAll('.product-result'));
    var secondaryImageWrappers = productCard.showSecondaryImage ? cards.map(function (card) {
      return card.querySelector('[data-ezoko-image-wrapper]');
    }).filter(Boolean) : [];
    var secondaryImageFallbacks = secondaryImageWrappers.filter(function (wrapper) {
      var image = wrapper.querySelector('.product-item__secondary-image');

      if (image) {
        prepareSecondaryImage(wrapper, image);
      }

      return !image;
    });

    portalizeQuickBuyElements(cards);
    preloadSecondaryImages(secondaryImageFallbacks);

    cards.forEach(function (card) {
      verifyQuickAddAvailability(card);

      if (card.dataset.ezokoUpgradeScheduled !== 'true' && window.customElements && typeof window.customElements.upgrade === 'function') {
        card.dataset.ezokoUpgradeScheduled = 'true';
        Promise.all([
          window.customElements.whenDefined('product-form'),
          window.customElements.whenDefined('loader-button')
        ]).then(function () {
          if (card.isConnected) {
            upgradeProductCardControls(card);
            syncDirectProductForm(card);
            [50, 250, 1000].forEach(function (delay) {
              window.setTimeout(function () {
                if (card.isConnected) {
                  syncDirectProductForm(card);
                }
              }, delay);
            });
          }
        });
      }

      bindQuickBuyButtons(card);
      syncDirectProductForm(card);

      var wrapper = card.querySelector('[data-ezoko-image-wrapper]');
      if (!wrapper || wrapper.dataset.ezokoCardEnhanced === 'true' || !productCard.showSecondaryImage) {
        return;
      }

      wrapper.dataset.ezokoCardEnhanced = 'true';
      wrapper.addEventListener('pointerenter', function () { loadSecondaryImage(wrapper); }, { once: true });
      wrapper.addEventListener('focusin', function () { loadSecondaryImage(wrapper); }, { once: true });
    });
  }

  function syncSort(config) {
    var select = document.querySelector('.ais-SortBy-select');
    var label = document.querySelector('[data-ezoko-sort-label]');
    var options = document.querySelector('[data-ezoko-sort-options]');
    var desktopLabel = document.querySelector('[data-ezoko-desktop-sort-label]');
    var desktopOptions = document.querySelector('[data-ezoko-desktop-sort-options]');
    var sortControls = document.querySelectorAll('.sort-control, .sort-trigger');

    sortControls.forEach(function (control) {
      control.classList.toggle('is-disabled', !select || select.options.length < 2);
    });

    if (!select) {
      if (label) {
        label.textContent = window.algoliaShopify.translations.relevance;
      }
      if (desktopLabel) {
        desktopLabel.textContent = config.instantSearch.relevance;
      }
      return;
    }

    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    Array.prototype.forEach.call(select.options, function (option) {
      if (option.value === 'shopify_products') {
        option.textContent = config.instantSearch.relevance;
      } else if (/recently_ordered_count_desc$/.test(option.value)) {
        option.textContent = config.instantSearch.mostPopular;
      }
    });

    if (label) {
      label.textContent = select.options[select.selectedIndex].text;
    }
    if (desktopLabel) {
      desktopLabel.textContent = select.options[select.selectedIndex].text;
    }

    if (options) {
      options.innerHTML = Array.prototype.map.call(select.options, function (option) {
        var selected = option.value === select.value;
        return '<button type="button" class="sort-radio-option' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-ezoko-sort-value="' + escapeAttribute(option.value) + '"><span class="radio" aria-hidden="true"></span>' + escapeAttribute(option.text) + '</button>';
      }).join('');
    }

    if (desktopOptions) {
      desktopOptions.innerHTML = Array.prototype.map.call(select.options, function (option) {
        var selected = option.value === select.value;
        return '<button type="button" class="sort-option' + (selected ? ' is-selected' : '') + '" role="option" aria-selected="' + selected + '" data-ezoko-sort-value="' + escapeAttribute(option.value) + '">' + escapeAttribute(option.text) + '</button>';
      }).join('');
    }
  }

  function syncLoading(algolia, config) {
    var root = document.querySelector('.ezoko-instantsearch-page');
    var stats = document.querySelector('.ais-stats-container');
    var status = algolia.instantSearch && algolia.instantSearch.search ? algolia.instantSearch.search.status : 'idle';

    window.clearTimeout(loadingTimer);

    if (status === 'loading' || status === 'stalled') {
      if (!loadingStartedAt) {
        loadingStartedAt = Date.now();
      }

      var loadingDelay = Math.max(0, 300 - (Date.now() - loadingStartedAt));
      loadingTimer = window.setTimeout(function () {
        if (root) {
          root.classList.add('is-updating');
        }
        if (stats) {
          stats.innerHTML = '<span class="results-loading"><span class="loading-spinner" aria-hidden="true"></span>' + escapeAttribute(config.instantSearch.updating) + '</span>';
        }
      }, loadingDelay);
    } else if (root) {
      loadingStartedAt = 0;
      root.classList.remove('is-updating');
    }
  }

  function updatePresentation(algolia, config) {
    var root = document.querySelector('.ezoko-instantsearch-page');
    var stats = document.querySelector('.ezoko-stats');

    if (!root || !stats) {
      return;
    }

    var query = stats.dataset.query || new URLSearchParams(window.location.search).get('q') || '';
    var count = Number(stats.dataset.nbHits || 0);
    var refinements = refinementData();
    var queryElement = document.querySelector('[data-ezoko-search-query]');
    var showResults = document.querySelector('[data-ezoko-show-results]');
    var headerInput = document.querySelector('#ezoko-algolia-search .aa-Input');

    syncRefinementHistory(refinements);
    root.classList.toggle('is-query-zero', count === 0 && refinements.length === 0);
    root.classList.toggle('is-filtered-zero', count === 0 && refinements.length > 0);

    if (count > 0 && refinements.length === 0) {
      lastHealthyCounts[query] = count;
    }

    if (queryElement) {
      queryElement.textContent = query;
    }
    if (showResults) {
      showResults.textContent = formatCount(config.instantSearch.showResults, count);
    }
    if (headerInput && document.activeElement !== headerInput && headerInput.value !== query) {
      headerInput.value = query;
    }

    enhanceAppliedFilters();
    document.querySelectorAll('[data-ezoko-toolbar-clear]').forEach(function (button) {
      button.hidden = refinements.length === 0;
    });
    cloneAppliedFilters();
    enhanceFacets(refinements);
    enhancePriceFacet(config);
    enhanceProductCards(config);
    syncSort(config);
    renderEmptyState(config, query, count, refinements);
    syncLoading(algolia, config);
    return true;
  }

  function openFilters(open) {
    var root = document.querySelector('.ezoko-instantsearch-page');
    var trigger = document.querySelector('[data-ezoko-open-filters]');

    if (!root || !trigger) {
      return;
    }

    var wasOpen = root.classList.contains('is-filter-open');

    window.clearTimeout(filterInteractionTimer);
    root.classList.toggle('is-filter-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('ezoko-filter-takeover-open', open);

    if (open) {
      document.body.classList.remove('ezoko-filter-takeover-closing');
      var closeButton = document.querySelector('[data-ezoko-close-filters]');
      if (closeButton) {
        closeButton.focus();
      }
    } else {
      if (wasOpen) {
        document.body.classList.add('ezoko-filter-takeover-closing');
        filterInteractionTimer = window.setTimeout(function () {
          document.body.classList.remove('ezoko-filter-takeover-closing');
        }, 400);
      }
      trigger.focus();
    }
  }

  function openSort(open) {
    var layer = document.getElementById('ezoko-mobile-sort');
    var trigger = document.querySelector('[data-ezoko-open-sort]');

    if (!layer || !trigger) {
      return;
    }

    layer.classList.toggle('is-open', open);
    layer.setAttribute('aria-hidden', open ? 'false' : 'true');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('ezoko-sort-sheet-open', open);

    if (open) {
      sortTrigger = trigger;
      var selected = layer.querySelector('.sort-radio-option.is-selected') || layer.querySelector('.sort-radio-option');
      if (selected) {
        selected.focus();
      }
    } else if (sortTrigger) {
      sortTrigger.focus();
      sortTrigger = null;
    }
  }

  function openDesktopSort(open) {
    var control = document.querySelector('[data-ezoko-desktop-sort]');
    var toggle = document.querySelector('[data-ezoko-toggle-desktop-sort]');
    var menu = document.querySelector('[data-ezoko-desktop-sort-options]');

    if (!control || !toggle || !menu) {
      return;
    }

    control.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    menu.hidden = !open;

    if (open) {
      var selected = menu.querySelector('.sort-option.is-selected') || menu.querySelector('.sort-option');
      if (selected) {
        selected.focus();
      }
    } else if (control.contains(document.activeElement) && document.activeElement !== toggle) {
      toggle.focus();
    }
  }

  function clearAllRefinements(attempts) {
    var refinement = refinementData()[0];
    var nextAttempt = Number(attempts || 0) + 1;

    if (!refinement || nextAttempt > 20) {
      return;
    }

    refinement.link.click();
    window.setTimeout(function () {
      clearAllRefinements(nextAttempt);
    }, 100);
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-ezoko-clear-filters], [data-ezoko-toolbar-clear], .clear-filters-button')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearAllRefinements();
  }, true);

  document.addEventListener('click', function (event) {
    var removeLast = event.target.closest('[data-ezoko-remove-last]');
    var sortOption = event.target.closest('[data-ezoko-sort-value]');
    var mobileRefinement = event.target.closest('.mobile-applied-filters .ais-current-refined-values--link');
    var desktopSortToggle = event.target.closest('[data-ezoko-toggle-desktop-sort]');
    var closeFiltersTrigger = event.target.closest('[data-ezoko-close-filters]');

    if (closeFiltersTrigger) {
      event.preventDefault();
      event.stopPropagation();
      openFilters(false);
    } else if (mobileRefinement) {
      event.preventDefault();
      var matchingRefinement = refinementData().find(function (item) {
        return item.key === [mobileRefinement.dataset.attribute, mobileRefinement.dataset.type, mobileRefinement.dataset.value].join('|');
      });
      if (matchingRefinement) {
        matchingRefinement.link.click();
      }
    } else if (event.target.closest('[data-ezoko-open-filters]')) {
      openFilters(true);
    } else if (event.target.closest('[data-ezoko-open-sort]')) {
      openSort(true);
    } else if (desktopSortToggle) {
      openDesktopSort(desktopSortToggle.getAttribute('aria-expanded') !== 'true');
    } else if (event.target.closest('[data-ezoko-close-sort]')) {
      openSort(false);
    } else if (removeLast) {
      var refinement = refinementData().find(function (item) { return item.key === removeLast.dataset.ezokoRemoveLast; });
      if (refinement) {
        refinement.link.click();
      }
    } else if (sortOption) {
      var select = document.querySelector('.ais-SortBy-select');
      if (select) {
        select.value = sortOption.dataset.ezokoSortValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      openSort(false);
      openDesktopSort(false);
    } else if (!event.target.closest('[data-ezoko-desktop-sort]')) {
      openDesktopSort(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    var facetLabel = event.target.closest('.ais-RefinementList-label');

    if (facetLabel && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      facetLabel.click();
    }

    if (event.key === 'Escape') {
      openFilters(false);
      openSort(false);
      openDesktopSort(false);
    }
  });

  document.addEventListener('algolia.hooks.initialize', function () {
    var algolia = window.algoliaShopify;
    var config = readConfig();

    if (!algolia || !config || !config.instantSearch || !algolia.hooks) {
      return;
    }

    registerHook(algolia, 'beforeAutocompleteOptions', function (options) {
      if (window.location.pathname.indexOf('/search') !== -1) {
        options.initialState = Object.assign({}, options.initialState, {
          query: new URLSearchParams(window.location.search).get('q') || ''
        });
      }
      return options;
    });

    registerHook(algolia, 'beforeInstantSearchMainTemplate', function (_defaultTemplate, data, html) {
      return mainTemplate(algolia, config, data, html);
    });

    registerHook(algolia, 'beforeInstantSearchProductTemplate', function (_defaultTemplate, hit, html, components, handleItemClick) {
      return productTemplate(algolia, config, hit, html, components, handleItemClick);
    });

    registerHook(algolia, 'beforeInstantSearchStatsTemplate', function (_defaultTemplate, data, html) {
      return statsTemplate(config, data, html);
    });

    registerHook(algolia, 'beforeInstantSearchNoResultTemplate', function (_defaultTemplate, html) {
      return noResultsTemplate(config, html);
    });

    registerHook(algolia, 'beforeInstantSearchFacetParamsOptions', function (params, facet) {
      return enhanceFacetParams(params, facet, config);
    });

    document.addEventListener('algolia.instantSearch.onRender', function () {
      window.requestAnimationFrame(function () {
        var presented = updatePresentation(algolia, config);
        var instance = algolia.instantsearch && algolia.instantsearch.search;
        var shell = document.querySelector('[data-ezoko-search-shell]');

        if (presented && instance && instance.status === 'idle' && shell && shell.dataset.state !== 'ready') {
          shell.dispatchEvent(new Event('ezoko:search-ready'));
        }
      });
    });

    document.addEventListener('algolia.instantSearch.afterInit', function () {
      var instance = algolia.instantsearch && algolia.instantsearch.search;
      if (instance) {
        instance.on('error', function () {
          var shell = document.querySelector('[data-ezoko-search-shell]');
          if (shell) {
            shell.dispatchEvent(new Event('ezoko:search-error'));
          }
        });
      }
      updatePresentation(algolia, config);
    });
  });
})();
